import { useMemo } from 'react'

import { EmployeeSelectorV2ItemsDocument } from 'generated/resources/graphql'

import Chip from 'modules/core/components/Filter/base/Chip'
import Content from 'modules/core/components/Filter/base/Content'
import Provider from 'modules/core/components/Filter/base/Provider'
import { useQuery } from 'modules/core/hooks/useQuery'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useFilter } from 'modules/performance_process/components/review/show/Filters/context'

interface Props {
  materializedTargetIds?: number[]
}

export const EmployeeFilter = ({ materializedTargetIds }: Props) => {
  const { value, setValue } = useFilter('employeeIds')

  const { data: selectedEmployeesData } = useQuery(EmployeeSelectorV2ItemsDocument, {
    variables: {
      ids: value?.length > 0 ? value : [],
    },
    skip: value?.length !== 1,
  })

  const selectedEmployees = useMemo(
    () =>
      selectedEmployeesData?.employees.employeesConnection.nodes.map((node) => node.employeeName) ??
      [],
    [selectedEmployeesData]
  )

  const activeLabel = useMemo(() => {
    if (value?.length === 0) {
      return '...'
    }

    if (selectedEmployees?.length === 1) {
      const employee = selectedEmployees[0]

      return getEmployeeDisplayName({
        fullName: employee?.fullName ?? '',
        lastName: employee?.lastName ?? '',
        preferredName: employee?.preferredName ?? '',
      })
    }

    return `(${value?.length})`
  }, [value, selectedEmployees])

  return (
    <>
      <EmployeeSelectorV2
        label={i18n.t('common.employee')}
        multiple
        value={value}
        onChange={(state) => {
          if (state.type === 'manual') {
            setValue(state.ids)
          }
        }}
        options={{
          disableSelectAll: true,
        }}
        forcedFilters={{
          employeeIds: materializedTargetIds,
        }}
      >
        <Provider
          value={value?.length > 0}
          onValueChange={() => null}
          defaultOpen={false}
          closeOnChange
        >
          <Chip.Toggler
            active={value?.length > 0}
            label={i18n.t('common.employee')}
            activeLabel={activeLabel}
          />
          <Content maxWidth="s" maxHeight="s600" />
        </Provider>
      </EmployeeSelectorV2>
    </>
  )
}
